import React, { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { CustomAccordionMenu } from "components";
import { downloadAllocationTemplate, uploadAllocationTemplate } from 'api';
import { EsgAddFile, EsgDownloadIcon, EsgUploadIcon } from 'assets';
import { useNotify } from 'hooks';

export const TemplateActionsButton = ({ allocationId }: { allocationId: number }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const queryClient = useQueryClient();
    const notify = useNotify();

    const { mutateAsync: exportCSV, isLoading: isDownloadingTemplate } = useMutation({
        mutationFn: downloadAllocationTemplate,
        onSuccess: async (response: string) => {
            try {
                const blob = new Blob([response], { type: "text/csv" });
                const utilityUrl = URL.createObjectURL(blob);

                const link = document.createElement("a");
                link.href = utilityUrl;
                link.download = `allocation_template_${allocationId}.csv`;
                link.click();

                URL.revokeObjectURL(utilityUrl);
                notify.success('Template downloaded successfully.');
            } catch (error) {
                notify.error('Failed to process the downloaded file.');
            }
        },
        onError: (error: AxiosError) => {
            notify.error(typeof error?.response?.data === 'string' ? error.response?.data : 'Failed to download template.');
        },
    });

    const { mutateAsync: uploadCSV, isLoading: isUploading } = useMutation({
        mutationFn: ({ id, file }: { id: number; file: File }) => uploadAllocationTemplate(id, file),
        onSuccess: () => {
            notify.success('CSV uploaded successfully.');
            queryClient.invalidateQueries(['emissionsAllocationDetails', allocationId]);
        },
        onError: (error: AxiosError) => {
            notify.error(typeof error.response?.data === 'string' ? error.response?.data : 'Failed to upload CSV.');
        },
    });

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (!file.name.endsWith('.csv')) {
                notify.error('Please select a CSV file.');
                return;
            }
            uploadCSV({ id: allocationId, file });
            // eslint-disable-next-line no-param-reassign
            event.target.value = '';
        }
    };

    const onUploadTemplateClick = () => {
        fileInputRef.current?.click();
    };

    const onDownloadTemplateClick = () => {
        exportCSV(allocationId);
    }

    return <><CustomAccordionMenu
        title="Template Actions"
        initialIcon={<EsgAddFile />}
        sx={{ width: '204px', height: '36px' }}
        buttons={[
            {
                label: (isDownloadingTemplate ? 'Downloading...' : 'Download Template'),
                icon: <EsgDownloadIcon />,
                disabled: isDownloadingTemplate,
                onClick: onDownloadTemplateClick,
            },
            {
                label: (isUploading ? 'Uploading...' : 'Upload Template'),
                icon: <EsgUploadIcon />,
                disabled: isUploading,
                onClick: onUploadTemplateClick,
            },
        ]}
    />

        <input
            type="file"
            accept=".csv"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileUpload}
        /></>
}